import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { z } from 'zod';
import { permissions } from 'shared/constants/permissions';
import { useCopilot } from 'shared/context/CopilotContext';
import { useCopilotFrontendTool, zodToolSchema } from '../hooks/useCopilotFrontendTool';
import { formatCopilotPageElementsDescription, type HighlightElementToolParams, type HighlightElementToolResult, mapCopilotPageElementsForToolResult } from 'shared/copilot/utils/copilotHighlightElementTool';

/**
 * Registers the `highlight_element` frontend tool for drawing attention to page UI.
 *
 * Validates `elementId` against registered page elements, applies a focus highlight,
 * scrolls the target into view, and lists available elements on failure.
 */
export const useHighlightElementTool = () => {
  const $ = _c(15);
  const {
    currentPageContext,
    highlightPageElement,
    scrollToPageElement
  } = useCopilot();
  currentPageContext?.elements;
  const t0 = currentPageContext?.elements;
  let t1;
  if ($[0] !== t0) {
    t1 = formatCopilotPageElementsDescription(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const availableElementsDescription = t1;
  const t2 = `Highlight a visible element on the page with a soft gradient outline and pulsing outer shadow. Use this whenever you reference a concrete page element in your response. After highlighting, also call inspect_current_page with the same elementId and briefly describe what the element shows (e.g. for a chart: a few notes on series/values/trend)—not a full data dump unless the user asks for detail. Available elements: ${availableElementsDescription}.`;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = zodToolSchema(z.object({
      elementId: z.string().describe("The id of the page element to highlight.")
    }));
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== currentPageContext?.elements || $[4] !== highlightPageElement || $[5] !== scrollToPageElement) {
    t4 = params => {
      const availableElements = mapCopilotPageElementsForToolResult(currentPageContext?.elements);
      const element = currentPageContext?.elements?.find(item => item.id === params.elementId);
      if (element == null) {
        return {
          success: false,
          message: `Unknown page element "${params.elementId}".`,
          availableElements
        };
      }
      const didHighlight = highlightPageElement(params.elementId);
      if (!didHighlight) {
        return {
          success: false,
          message: `Could not highlight "${element.label}" because its element is not mounted.`,
          availableElements
        };
      }
      scrollToPageElement(params.elementId);
      return {
        success: true,
        message: `Highlighted ${element.label}.`,
        availableElements
      };
    };
    $[3] = currentPageContext?.elements;
    $[4] = highlightPageElement;
    $[5] = scrollToPageElement;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4) {
    t5 = {
      name: "highlight_element",
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      description: t2,
      parameters: t3,
      handler: t4
    };
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== availableElementsDescription || $[11] !== currentPageContext || $[12] !== highlightPageElement || $[13] !== scrollToPageElement) {
    t6 = [availableElementsDescription, currentPageContext, highlightPageElement, scrollToPageElement];
    $[10] = availableElementsDescription;
    $[11] = currentPageContext;
    $[12] = highlightPageElement;
    $[13] = scrollToPageElement;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  useCopilotFrontendTool(t5, t6);
};